import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalFooter,
  Button,
  useDisclosure,
  Chip,
  Pagination,
  Tabs,
  Tab,
  Divider,
  Spinner,
} from "@nextui-org/react";
import { FiDownload, FiSearch, FiTrash } from "react-icons/fi";
import { columns, statusOptions } from "../data/quotesData";
import { useDispatch, useSelector } from "react-redux";
import { changeStatus, getAllQuotes } from "../store/slices/supplierSlice";
import axios from "axios";
import { displayDate, formatTime } from "../utils";
import { STATUS } from "../store/constants";
import { MdOutlineFileDownload } from "react-icons/md";

const statusColorMap = {
  // eslint-disable-next-line no-useless-computed-key
  ["awaiting approval"]: "warning",
  // eslint-disable-next-line no-useless-computed-key
  ["in progress"]: "warning",
  accepted: "success",
  dispute: "danger",
  rejected: "danger",
  discarded: "danger",
  cancelled: "danger",
  ["Order created"]: "secondary",
};

// visible columns in the table
const INITIAL_VISIBLE_COLUMNS = [
  "status",
  "industry",
  "company",
  "car",
  "part",
  "duration",
  "delete",
];

const Quotes = () => {
  const accessToken = localStorage.getItem("accessToken");
  const dispatch = useDispatch();
  const { status } = useSelector((state) => state.supplier);
  const [quotes, setQuotes] = useState([]);
  const [allQuotes, setAllQuotes] = useState([]);
  const [filterValue, setFilterValue] = useState("");
  let role = localStorage.getItem("role");
  const [visibleColumns, setVisibleColumns] = useState(() => {
    let columns = [...INITIAL_VISIBLE_COLUMNS];
    if (role === "CUSTOMER") {
      columns = columns.filter((col) => col !== "delete");
    }
    return new Set(columns);
  });
  const [statusFilter, setStatusFilter] = useState("all");
  const [rowsPerPage, setRowsPerPage] = useState(6);
  const [sortDescriptor, setSortDescriptor] = useState({
    column: "age",
    direction: "ascending",
  });
  // const [page, setPage] = useState(1);
  const [page, setPage] = React.useState(() => {
    const stored = localStorage.getItem("currentPage");
    return stored ? Number(stored) : 1; // default to 1 when nothing stored
  });
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState("");
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const { isOpen, onOpen, onOpenChange } = useDisclosure();
  const showModal = (id) => {
    setDeleteId(id);
    setIsModalVisible(true);
    onOpen();
  };

  const fetchAllQuotes = useCallback(async () => {
    try {
      dispatch(changeStatus(STATUS.LOADING));

      const first = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/quotes?page=1&pageSize=1`,
        { headers: { authorization: `Bearer ${accessToken}` } }
      );

      const totalRows = first.data?.details?.index?.totalRecords || 0;

      const res = await axios.get(
        `${process.env.REACT_APP_SERVER_URL}/quotes?page=1&pageSize=${totalRows}`,
        { headers: { authorization: `Bearer ${accessToken}` } }
      );

      setAllQuotes(res.data?.details?.rows || []);
      dispatch(changeStatus(STATUS.IDLE));
    } catch (e) {
      dispatch(changeStatus(STATUS.ERROR));
      console.error(e);
    }
  }, [accessToken, dispatch]);
  useEffect(() => {
    fetchAllQuotes();
    const id = setInterval(fetchAllQuotes, 120000);
    return () => clearInterval(id);
  }, []);

  const handleDelete = async (id) => {
    const accessToken = localStorage.getItem("accessToken");

    if (!accessToken) {
      console.error("No access token found. Please login.");
      return;
    }

    try {
      const response = await axios.put(
        `${process.env.REACT_APP_SERVER_URL_V2}/quotes/deletequote/${id}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      setAllQuotes((prev) => prev.filter((q) => q.id !== id));

      if (response.status === 200) {
        console.log("Quote deleted successfully:", id);

        setQuotes((prev) => prev.filter((quote) => quote.id !== id));
      } else {
        console.error("Failed to delete quote:", response);
      }
    } catch (error) {
      console.error(
        "Error deleting quote:",
        error.response ? error.response.data : error.message
      );
      if (error.response && error.response.status === 401) {
        console.error(
          "Unauthorized - please check your login credentials or token."
        );
      }
    }
  };

  const handleDownloadCSV = () => {
    const rowsToExport = sortedItems;

    const header = [
      "Quote ID",
      "Reference",
      "Status",
      "Industry",
      "Car",
      "Part",
      "Created At",
    ];

    const lines = rowsToExport.map((r) => {
      const make = r.Request.VehicleMake?.name || r.Request.custom_make || "";
      const model =
        r.Request.VehicleModel?.name || r.Request.custom_model || "";
      const industry = r?.Request?.User?.Industry || "Not Provided";
      const created = new Date(r?.createdAt).toLocaleString();
      const part = r.Request.Part?.name || r.Request.custom_part_type || "";
      const cells = [
        r?.id || "Not Provided",
        r?.reference_id || "Not Provided",
        r?.QuoteStatus?.status || "Not Provided",
        industry,
        `${make} ${model}`,
        part,
        created,
      ];
      return cells.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",");
    });

    const csvContent = [header.join(","), ...lines].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "quotes_report.csv";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleCancel = () => {
    setIsModalVisible(false); // Close the modal if the user clicks 'No'
  };

  const handleOk = () => {
    handleDelete(deleteId);
    setIsModalVisible(false);
  };

  // useEffect(() => {
  //   const storedPage = localStorage.getItem("currentPage");
  //   if (storedPage) {
  //     setPage(Number(storedPage));
  //   }
  // }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      fetchAllQuotes(page, rowsPerPage);
    }, 120000);

    return () => clearInterval(interval);
  }, [page, rowsPerPage]);
  useEffect(() => {
    fetchAllQuotes(page, rowsPerPage);
  }, [dispatch, accessToken, totalPages, page, rowsPerPage]);

  useEffect(() => {}, [quotes, totalPages, status]);

  const hasSearchFilter = Boolean(filterValue);

  const headerColumns = useMemo(() => {
    if (visibleColumns === "all") {
      return columns;
    }
    let column = [...columns];

    if (role === "SUPPLIER") {
      column = column.filter((item) => {
        return item.uid !== "company";
      });
    } else if (role === "CUSTOMER") {
      column = column.filter((item) => {
        return item.uid !== "industry";
      });
    }

    return column.filter((colum) =>
      Array.from(visibleColumns).includes(colum.uid)
    );
  }, [visibleColumns]);

  const filteredItems = useMemo(() => {
    let data = [...allQuotes];

    if (statusFilter !== "all") {
      data = data.filter(
        (q) => q?.QuoteStatus?.status.toLowerCase() === statusFilter
      );
    }

    if (filterValue) {
      const v = filterValue.toLowerCase();
      data = data.filter(
        (q) =>
          q?.Request?.VehicleMake?.name?.toLowerCase().includes(v) ||
          q?.Request?.VehicleModel?.name?.toLowerCase().includes(v) ||
          q?.Request?.Part?.name?.toLowerCase().includes(v) ||
          q?.reference_id?.toLowerCase().includes(v)
      );
    }

    return data;
  }, [allQuotes, statusFilter, filterValue]);
  useEffect(() => {
    const pagesNow = Math.max(Math.ceil(filteredItems.length / rowsPerPage), 1);
    if (page > pagesNow) {
      setPage(1);
      localStorage.setItem("currentPage", 1);
    }
  }, [filteredItems.length, rowsPerPage]);

  const pages = Math.max(Math.ceil(filteredItems.length / rowsPerPage), 1);
  const items = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return filteredItems.slice(start, end);
  }, [filteredItems, page, rowsPerPage]);
  const sortedItems = useMemo(() => {
    return [...items].sort((a, b) => {
      const first = a[sortDescriptor.column];
      const second = b[sortDescriptor.column];
      const cmp = first < second ? -1 : first > second ? 1 : 0;

      return sortDescriptor.direction === "descending" ? -cmp : cmp;
    });
  }, [sortDescriptor, items]);

  const renderCell = useCallback((data, columnKey) => {
    const cellValue = data[columnKey];

    switch (columnKey) {
      case "status":
        return (
          <Chip
            className="capitalize"
            color={statusColorMap[data?.QuoteStatus?.status.toLowerCase()]}
            variant="flat"
          >
            {data?.QuoteStatus?.status}
          </Chip>
        );

      case "company":
        return (
          role === "CUSTOMER" && (
            <div className="">
              <p className="table_cell">
                {data.User?.company_name || "Not Provided"}
              </p>
            </div>
          )
        );

      case "industry":
        return (
          role === "SUPPLIER" && (
            <div className="">
              {/* <p className="table_cell">{data.Request?.User?.first_name + " " + data.Request?.User?.last_name || "Not Provided"}</p> */}
              <p className="table_cell">
                {data.Request?.User?.Industry || "Not Provided"}
              </p>
            </div>
          )
        );

      case "car":
        return (
          <div className="flex flex-col gap-1 capitalize ">
            <p className="table_cell capitalize">
              {data?.Request?.VehicleMake
                ? data?.Request?.VehicleMake.name.toLowerCase()
                : data?.Request?.custom_make.toLowerCase() ||
                  "Not Provided"}{" "}
              {data?.Request?.VehicleModel
                ? data?.Request?.VehicleModel.name.toLowerCase()
                : data?.Request?.custom_model.toLowerCase() || "Not Provided"}
            </p>
          </div>
        );

      case "part":
        return (
          <div>
            <p className="table_cell capitalize ">
              {data?.Request?.Part
                ? data?.Request?.Part?.name.toLowerCase()
                : data?.Request?.custom_part_type.toLowerCase() ||
                  "Not Provided"}
            </p>
          </div>
        );

      case "duration":
        return (
          <div className="flex flex-col gap-1 ">
            <p className="table_cell capitalize">
              {formatTime(data?.createdAt || "Not Provided")}
            </p>
            <p className="table_cell_sm">{displayDate(data?.createdAt)}</p>
          </div>
        );
      // case "delete":
      //   if (role === "CUSTOMER") return null;
      //   return (
      //     <div className="flex items-center gap-2 ">
      //       <button onClick={() => handleDelete(data.id)}>
      //         <FiTrash className="text-red-500 cursor-pointer" />
      //       </button>
      //     </div>
      //   );
      case "delete":
        if (role === "CUSTOMER") return null;
        return (
          <div className="flex items-center gap-2 text-lg ">
            {/* <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDelete(data.id);
              }}
            >
              <FiTrash className="text-red-500 cursor-pointer" />
            </button> */}
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                showModal(data.id);
              }}
            >
              <FiTrash className="text-red-500 cursor-pointer" />
            </button>
          </div>
        );
      default:
        return cellValue;
    }
  }, []);

  const onRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
    fetchAllQuotes(1, e.target.value);
  }, []);

  const onSearchChange = useCallback((value) => {
    if (value) {
      setFilterValue(value);
      // setPage(1);
    } else {
      setFilterValue("");
    }
  }, []);

  const onClear = useCallback(() => {
    setFilterValue("");
    // setPage(1);
  }, []);

  const topContent = useMemo(() => {
    return (
      <div className="flex flex-col gap-6">
        <div className="w-full flex justify-between items-center gap-3">
          <Input
            isClearable
            className="flex-1 sm:max-w-[44%]"
            placeholder="Search Quotes"
            startContent={<FiSearch />}
            value={filterValue}
            onClear={() => onClear()}
            onValueChange={onSearchChange}
          />

          {role === "SUPPLIER" && (
            <Button
              startContent={<MdOutlineFileDownload />}
              onPress={handleDownloadCSV}
              disabled={status === STATUS.LOADING || sortedItems.length === 0}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm"
            >
              {status === STATUS.LOADING ? "Loading…" : "Download CSV"}
            </Button>
          )}
        </div>

        <div className="flex flex-col-reverse sm:flex-row flex-wrap items-center justify-between gap-y-4">
          <div>
            <Tabs
              aria-label="Options"
              variant="underlined"
              selectedKey={statusFilter}
              // onSelectionChange={setStatusFilter}
              onSelectionChange={(key) => {
                setStatusFilter(key);
                setPage(1);
                // setFilterValue("");
              }}
              className="mobile-only:[&>div]:grid mobile-only:[&>div]:grid-cols-2 mobile-only:[&>div]:gap-2"
            >
              <Tab key="all" title="All Quotes"></Tab>
              <Tab key="awaiting approval" title="Awaiting Approval"></Tab>
              <Tab key="in progress" title="In Progress"></Tab>
              <Tab key="accepted" title="Quotes Accepted"></Tab>
            </Tabs>
          </div>

          <div className="flex items-center gap-5 text-xs sm:text-sm text-default-400">
            <span>Total: {filteredItems.length} Quotes</span>
            <Divider orientation="vertical" className="h-6" />
            <label className="flex items-center">
              Rows Per Page:
              <select
                className="bg-transparent outline-none"
                onChange={onRowsPerPageChange}
              >
                <option value="6">6</option>
                <option value="10">10</option>
                <option value="15">15</option>
              </select>
            </label>
          </div>
        </div>
      </div>
    );
  }, [
    filterValue,
    onSearchChange,
    statusFilter,
    quotes.length,
    onRowsPerPageChange,
    onClear,
    status, // ← so the button label + disabled state update
    sortedItems, // ← so you get fresh data as soon as it arrives
    handleDownloadCSV, // ← ensures the latest handler closure
    role,
  ]);

  const bottomContent = useMemo(() => {
    return (
      <div className="py-2 px-2 flex justify-center items-center">
        <Pagination
          key={`${page}-${totalPages}`}
          isCompact
          showControls
          showShadow
          color="primary"
          page={page}
          total={pages}
          onChange={(p) => {
            setPage(p);
            localStorage.setItem("currentPage", p);
          }}
        />
      </div>
    );
  }, [page, pages]);

  return (
    <>
      <Table
        aria-label="Quotes Table"
        isHeaderSticky
        isStriped
        topContent={topContent}
        topContentPlacement="outside"
        bottomContent={bottomContent}
        bottomContentPlacement="outside"
        sortDescriptor={sortDescriptor}
        onSortChange={setSortDescriptor}
        classNames={{
          td: "whitespace-nowrap",
        }}
      >
        <TableHeader columns={headerColumns}>
          {(column) => (
            <TableColumn
              key={column.uid}
              align={column.uid === "actions" ? "center" : "start"}
              allowsSorting={column.sortable}
            >
              {column.name}
            </TableColumn>
          )}
        </TableHeader>
        <TableBody
          emptyContent={"No Quotes Found"}
          items={sortedItems}
          isLoading={status === STATUS.LOADING && !quotes.length}
          loadingContent={
            <Spinner label="Loading..." className="w-full bg-white" />
          }
        >
          {(item) => (
            <TableRow key={item.id}>
              {(columnKey) => (
                <TableCell>
                  {role === "SUPPLIER" ? (
                    <Link to={`/quotes/${item.id}`}>
                      {renderCell(item, columnKey)}
                    </Link>
                  ) : role === "CUSTOMER" ? (
                    <Link to={`/updateStatus/${item.id}`}>
                      {renderCell(item, columnKey)}
                    </Link>
                  ) : (
                    renderCell(item, columnKey)
                  )}
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>
      <Modal isOpen={isOpen} onOpenChange={onOpenChange}>
        <ModalContent>
          {(onClose) => (
            <>
              <ModalHeader>Confirm Delete</ModalHeader>
              <ModalBody>Are You Sure You Want To Delete This Quote?</ModalBody>
              <ModalFooter>
                <Button
                  color="primary"
                  onPress={() => {
                    handleDelete(deleteId);
                    onClose(); // closes modal after deletion
                  }}
                >
                  Yes
                </Button>
                <Button variant="light" onPress={onClose}>
                  No
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};

export default Quotes;
